import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import TrustStrip from "@/components/TrustStrip";
import NotificationsCenter from "@/components/NotificationsCenter";
import CommandPalette from "@/components/CommandPalette";
import { Button } from "../components/ui/button";
import {
  LayoutDashboard, Building2, PenSquare, Compass, Calendar, CreditCard,
  Shield, LogOut, Menu, X, ChevronRight, Sparkles, Settings as SettingsIcon, Search,
} from "lucide-react";
import { useState } from "react";

const NAV_MAIN = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Vue d'ensemble", end: true, key: "overview" },
  { to: "/dashboard/profile", icon: Building2, label: "Profil business", key: "profile" },
];
const NAV_GROWTH = [
  { to: "/dashboard/generator", icon: PenSquare, label: "Studio de contenu", key: "generator" },
  { to: "/dashboard/audit", icon: Compass, label: "Audit de visibilité", key: "audit" },
  { to: "/dashboard/calendar", icon: Calendar, label: "Calendrier", key: "calendar" },
];
const NAV_ACCOUNT = [
  { to: "/dashboard/pricing", icon: CreditCard, label: "Abonnement", key: "pricing" },
  { to: "/dashboard/settings", icon: SettingsIcon, label: "Paramètres", key: "settings" },
];

function NavGroup({ title, items, onClick }) {
  return (
    <div className="mb-1">
      <p className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground/70">{title}</p>
      <div className="space-y-0.5">
        {items.map(n => (
          <NavLink key={n.key} to={n.to} end={n.end} onClick={onClick}
            className={({ isActive }) => `group relative flex items-center gap-2.5 px-3 py-2 text-sm rounded-md transition-all ${
              isActive
                ? "bg-gradient-to-r from-primary/10 to-violet-500/5 text-foreground font-semibold"
                : "text-muted-foreground hover:bg-accent hover:text-foreground"
            }`}
            data-testid={`nav-${n.key}`}>
            {({ isActive }) => (
              <>
                {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r bg-gradient-to-b from-primary to-violet-500" />}
                <n.icon className={`h-4 w-4 ${isActive ? "text-primary" : ""}`} />
                <span className="flex-1">{n.label}</span>
                {isActive && <ChevronRight className="h-3.5 w-3.5 text-primary" />}
              </>
            )}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  const Sidebar = () => (
    <div className="h-full flex flex-col">
      <div className="px-5 h-16 flex items-center border-b border-border"><Logo size={20} /></div>
      <nav className="flex-1 p-3 space-y-3 overflow-y-auto">
        <NavGroup title="Pilotage" items={NAV_MAIN} onClick={close} />
        <NavGroup title="Croissance" items={NAV_GROWTH} onClick={close} />
        <NavGroup title="Compte" items={NAV_ACCOUNT} onClick={close} />
        {user?.role === "admin" && (
          <NavGroup title="Admin" items={[{ to: "/dashboard/admin", icon: Shield, label: "Administration", key: "admin" }]} onClick={close} />
        )}
      </nav>

      {/* Plan upsell card */}
      {user?.plan === "free" && (
        <div className="mx-3 mb-3 p-4 rounded-xl border border-border bg-gradient-to-br from-primary/10 via-violet-500/5 to-transparent relative overflow-hidden">
          <div className="absolute -top-8 -right-8 h-20 w-20 rounded-full bg-primary/20 blur-2xl" />
          <div className="relative">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-primary mb-1.5">
              <Sparkles className="h-3 w-3" /> Pro
            </div>
            <p className="text-sm font-semibold leading-tight">Débloquez la croissance</p>
            <p className="text-xs text-muted-foreground mt-1">Contenus illimités, audit complet, calendrier.</p>
            <Link to="/dashboard/pricing">
              <Button size="sm" className="w-full rounded-full mt-3 btn-glow h-8 text-xs" data-testid="upgrade-cta">
                Passer Pro
              </Button>
            </Link>
          </div>
        </div>
      )}

      <div className="p-3 border-t border-border">
        <div className="flex items-center gap-2.5 px-2 py-2">
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-primary to-violet-500 text-white flex items-center justify-center font-bold text-sm">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate" data-testid="user-name">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
              <span className={`h-1.5 w-1.5 rounded-full ${user?.plan === "free" ? "bg-muted-foreground/50" : "bg-emerald-500"}`} />
              {user?.plan === "free" ? "Plan gratuit" : `Plan ${user?.plan}`}
            </div>
          </div>
        </div>
        <Button variant="ghost" size="sm" className="w-full justify-start rounded-md mt-1 text-muted-foreground hover:text-foreground"
          onClick={async () => { await logout(); nav("/"); }} data-testid="logout-btn">
          <LogOut className="h-3.5 w-3.5 mr-2" />Déconnexion
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background" data-testid="dashboard-layout">
      <CommandPalette />
      <aside className="hidden md:flex w-64 border-r border-border flex-col bg-card/40 backdrop-blur-sm">
        <Sidebar />
      </aside>
      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="w-72 bg-background border-r border-border"><Sidebar /></div>
          <div className="flex-1 bg-black/50 backdrop-blur-sm" onClick={close} />
        </div>
      )}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border flex items-center justify-between px-4 lg:px-8 glass-strong sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setOpen(o => !o)} data-testid="mobile-menu-btn">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-muted-foreground hidden sm:block">Pilotage</p>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={() => {
              const ev = new KeyboardEvent("keydown", { key: "k", metaKey: true });
              document.dispatchEvent(ev);
            }}
              className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border text-xs text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
              data-testid="command-palette-btn">
              <Search className="h-3.5 w-3.5" />Rechercher
              <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-border bg-muted/50">⌘K</kbd>
            </button>
            <NotificationsCenter />
            <Link to="/" className="hidden sm:block">
              <Button variant="ghost" size="sm" className="rounded-full text-muted-foreground">Site public</Button>
            </Link>
            <ThemeToggle />
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8 max-w-7xl w-full mx-auto">
          <Outlet />
        </main>
        <footer className="border-t border-border px-4 lg:px-8 py-5 max-w-7xl w-full mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">© 2026 Local Pulse · Fait en France</p>
            <TrustStrip compact />
          </div>
        </footer>
      </div>
    </div>
  );
}
